import React, { useEffect, useRef, useState, useMemo } from 'react'
import Paper from '@mui/material/Paper'
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  Tooltip,
  IconButton,
  Box,
  lighten,
  useTheme
} from '@mui/material'
import { Add } from '@mui/icons-material'
import { addObject, deleteObject, editObject } from '../../../api/apiClientCrud'
import MyTableCellHeader from './MyTableCellHeader'
import MyTableEditableRow from './MyTableEditableRow'
import MyTablePaginationActions from './MyTablePaginationActions'
import { useTableSort } from '../../hooks/useTableSort'

function MyTableEditable({ mode = 'edit', data, setData, field, tableOptions, activePage = 0 }) {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [dataSet, setDataSet] = useState(data[field?.name] || [])
  const [editMode, setEditMode] = useState(false)
  const [rowId, setRowId] = useState(null)
  const [editingRow, setEditingRow] = useState({})
  const [previousRow, setPreviousRow] = useState({})
  const [isSubmit, setIsSubmit] = useState(false)
  const [isChanged, setIsChanged] = useState(false)
  const childRef = useRef([])
  const theme = useTheme()
  const WHITE_COLOR = '#FFFFFF'
  const ODD_ROW_COLOR = lighten(theme.palette.primary.main, 0.85)
  const { sortedData, sortedColumn, sortedColumnMode, handleSort } = useTableSort(dataSet, tableOptions.columns)

  const headerStyle = { height: '30px', padding: '10px' }
  const paperStyle = tableOptions.paperStyle || { width: '90%', marginLeft: '5%' }
  const tableStyle = useMemo(
    () =>
      tableOptions?.tableStyle || {
        maxHeight: '100%',
        height: '100%',
        overflowY: 'auto'
      },
    [tableOptions]
  )

  const getRowById = (id) => dataSet.find((row) => row.id === id || row.id_tmp === id)

  const updateData = (newData) => {
    setDataSet(newData)
    setData?.(newData)
  }

  const handleChangePage = (event, newPage) => setPage(newPage)

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const add = () => {
    const id = Date.now()
    const newRow = { id_tmp: id }
    const newDataSet = [...dataSet, newRow]
    setDataSet(newDataSet)
    setPreviousRow({})
    setEditingRow({ ...newRow })
    setRowId(id)
    setEditMode(true)
    setPage(Math.floor(newDataSet.length / rowsPerPage))
  }

  const deleteRow = (id) => {
    const oldData = [...dataSet]
    const idx = oldData.findIndex((obj) => obj.id === id || obj.id_tmp === id)
    if (idx === -1) return

    const row = oldData[idx]
    if (tableOptions.object && row.id !== undefined) {
      deleteObject(tableOptions.object, row.id)
    }

    oldData.splice(idx, 1)
    updateData(oldData)
    setEditMode(false)

    const maxPage = Math.max(0, Math.ceil(oldData.length / rowsPerPage) - 1)
    if (page > maxPage) {
      setPage(maxPage)
    }
  }

  const cancel = () => {
    const oldData = [...dataSet]
    const idx = oldData.findIndex((obj) => obj.id === rowId || obj.id_tmp === rowId)
    if (idx === -1) return

    const row = oldData[idx]
    const isNewRow = row.id === undefined && row.id_tmp !== undefined

    if (isNewRow && Object.keys(previousRow).length === 0) {
      oldData.splice(idx, 1)
    } else {
      oldData[idx] = previousRow
    }

    updateData(oldData)
    const maxPage = Math.max(0, Math.ceil(oldData.length / rowsPerPage) - 1)
    setPage((prev) => Math.min(prev, maxPage))
    setEditMode(false)
    setRowId(null)
    setEditingRow({})
    setPreviousRow({})
  }

  const onValueChange = (e) => {
    const { name, type, checked, value } = e.target
    const newValue = type === 'checkbox' ? checked : value
    const line = getRowById(rowId)
    if (!line) return

    const updatedLine = { ...line, [name]: newValue }
    setEditingRow(updatedLine)
    setIsChanged(true)
  }

  const onDateChange = (name) => (newValue) => {
    const line = getRowById(rowId)
    if (!line) return

    const updatedLine = { ...line, [name]: newValue }
    setEditingRow(updatedLine)
    setIsChanged(true)
  }

  const isRowEditing = (row) => (row.id === rowId || row.id_tmp === rowId) && editMode

  const save = () => setIsSubmit(true)

  const validate = async () => {
    if (!childRef.current) return false
    const promises = childRef.current.filter((ref) => ref?.validateField).map((ref) => ref.validateField())
    const results = await Promise.all(promises)
    return results.some((res) => res !== null)
  }

  useEffect(() => {
    if (isChanged) {
      const updatedRow = { ...editingRow }
      tableOptions.columns.forEach((field) => {
        if (field.defaultValueFunction) {
          updatedRow[field.name] = field.defaultValueFunction(updatedRow)
        }
      })

      const newDataSet = dataSet.map((row) =>
        row.id === rowId || row.id_tmp === rowId ? { ...row, ...updatedRow } : row
      )
      setDataSet(newDataSet)
      setEditingRow(updatedRow)
      setIsChanged(false)
    }
  }, [isChanged]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (rowId !== null) setEditMode(true)
  }, [rowId])

  useEffect(() => {
    setPage(activePage)
  }, [activePage])

  useEffect(() => {
    const parentRows = data[field?.name] || []

    setDataSet((currentDataSet) => {
      const unsavedRows = currentDataSet.filter((row) => row.id_tmp && !parentRows.some((r) => r.id === row.id))
      const preservedUnsavedRows = unsavedRows.filter((row) => row.id_tmp !== rowId)
      let newDataSet = [...parentRows, ...preservedUnsavedRows]
      const isEditingUnsavedRow = rowId && !parentRows.some((row) => row.id === rowId) && editingRow?.id_tmp === rowId

      if (isEditingUnsavedRow) {
        const idx = newDataSet.findIndex((row) => row.id_tmp === rowId)

        if (idx !== -1) {
          newDataSet[idx] = editingRow
        } else {
          newDataSet.push(editingRow)
        }
      }

      return newDataSet
    })
  }, [data, field, rowId, editingRow])

  useEffect(() => {
    if (!editingRow || !rowId) return
    setDataSet((prev) =>
      prev.map((row) => (row.id === rowId || row.id_tmp === rowId ? { ...row, ...editingRow } : row))
    )
  }, [editingRow, rowId])

  useEffect(() => {
    if (!isSubmit) return

    const callValidate = async () => {
      const isErr = await validate()
      if (!isErr) {
        let newData = [...dataSet]
        const idx = newData.findIndex((obj) => obj.id === rowId || obj.id_tmp === rowId)

        if (idx !== -1) {
          newData[idx] = { ...newData[idx], ...editingRow }
        } else {
          newData.push(editingRow)
        }

        if (!tableOptions.object) {
          updateData(newData)
          setEditMode(false)
          setRowId(null)
        } else {
          if (editingRow.id !== undefined) {
            editObject(tableOptions.object, editingRow.id, editingRow)
              .then((res) => {
                newData[idx] = res.data
                setDataSet(newData)
                setEditMode(false)
                setRowId(null)
              })
              .catch()
          } else {
            addObject(tableOptions.object, editingRow)
              .then((res) => {
                if (idx !== -1) newData[idx] = res.data
                else newData.push(res.data)
                setDataSet(newData)
                setEditMode(false)
                setRowId(null)
              })
              .catch()
          }
        }
      }
    }

    callValidate()
    setIsSubmit(false)
  }, [isSubmit]) // eslint-disable-line react-hooks/exhaustive-deps

  const memoizedRows = useMemo(() => {
    childRef.current = []
    return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, i) => {
      const isStripe = i % 2 !== 0
      const rowBgColor = isStripe ? ODD_ROW_COLOR : WHITE_COLOR
      return (
        <MyTableEditableRow
          key={row.id_tmp ?? row.id ?? i}
          row={row}
          index={i}
          mode={mode}
          tableOptions={tableOptions}
          editMode={editMode}
          rowId={rowId}
          editingRow={editingRow}
          onValueChange={onValueChange}
          onDateChange={onDateChange}
          setEditingRow={setEditingRow}
          childRef={childRef}
          isRowEditing={isRowEditing}
          setEditMode={setEditMode}
          setRowId={setRowId}
          setPreviousRow={setPreviousRow}
          save={save}
          deleteRow={deleteRow}
          cancel={cancel}
          rowBgColor={rowBgColor}
        />
      )
    })
  }, [sortedData, page, rowsPerPage, editMode, rowId, editingRow, tableOptions, mode]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Box sx={{ ...paperStyle, display: 'flex', flexDirection: 'row-reverse' }}>
        {mode === 'edit' && tableOptions.actions?.add && (
          <Tooltip title='Ajouter'>
            <span>
              <IconButton aria-label='add' onClick={add} disabled={editMode}>
                <Add />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Paper
        variant='outlined'
        style={{
          ...paperStyle,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        <TableContainer
          style={{
            ...tableStyle,
            flex: 1,
            overflowY: 'auto',
            minHeight: 0
          }}
        >
          <Table stickyHeader sx={{ tableLayout: 'fixed', minWidth: '100%' }}>
            <TableHead>
              <TableRow sx={headerStyle}>
                {tableOptions.isExpand && <TableCell sx={headerStyle} padding='checkbox' />}
                {tableOptions.columns.map(
                  (column, i) =>
                    (column.display === undefined || column.display !== false) && (
                      <MyTableCellHeader
                        key={`${i}-${column.name}-header`}
                        header={column}
                        sort={handleSort}
                        sortedColumn={sortedColumn}
                        sortedColumnMode={sortedColumnMode}
                      />
                    )
                )}
                {tableOptions.actions && mode === 'edit' && (
                  <MyTableCellHeader
                    header={{
                      label: 'Actions',
                      isSorting: false
                    }}
                    resize={false}
                  />
                )}
              </TableRow>
            </TableHead>
            <TableBody>{memoizedRows}</TableBody>
          </Table>
        </TableContainer>

        {tableOptions.showTotal?.display && (
          <TableContainer>
            <Table>
              <TableFooter>
                <TableRow>
                  <TableCell align='right' sx={{ width: '90%' }}>
                    {tableOptions.showTotal.label}
                  </TableCell>
                  <TableCell sx={{ width: '10%' }} align='left'>
                    {dataSet[tableOptions.showTotal.field]}
                    {tableOptions.showTotal.type === 'currency' && '€'}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          rowsPerPage={rowsPerPage}
          page={page}
          count={dataSet.length}
          component='div'
          labelRowsPerPage='Lignes par page :'
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`}
          ActionsComponent={MyTablePaginationActions}
        />
      </Paper>
    </>
  )
}

export default MyTableEditable
